/**
 * Group Management Service
 * 
 * Handles all group-related API calls including listing,
 * creating, updating, and deleting groups
 */

import { api, handleApiError } from './api';
import { statusToAPI, statusFromAPI } from '@/utils';
import type {
  GroupListItem,
  GroupFilters,
  GroupStats,
  CreateGroupPayload,
  UpdateGroupPayload,
  PaginationParams,
  PaginatedResponse,
  AgentListItem,
  CertificateListItem,
} from '@/types';

/**
 * Group Service Object
 * Contains all group management API operations
 */
export const groupService = {
  /**
   * Get Groups List
   * Fetches paginated list of groups with optional filters
   * 
   * @param params - Pagination parameters
   * @param filters - Optional filters for groups
   * @returns Promise with paginated group list
   */
  getGroups: async (
    params: PaginationParams,
    filters?: GroupFilters
  ): Promise<PaginatedResponse<GroupListItem>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
        ...(params.search && { search: params.search }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.organizationId && { 
          organizationId: filters.organizationId.toString() 
        }),
        ...(filters?.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters?.dateTo && { dateTo: filters.dateTo }),
      });

      const response = await api.get<PaginatedResponse<GroupListItem>>(
        `/groups?${queryParams.toString()}`
      );

      // Log para debug (remover em produção)
      if (import.meta.env.DEV) {
        console.log('📊 Groups API Response:', response.data);
      }

      // Verificar se a resposta tem o formato esperado
      if (!response.data) {
        throw new Error('Invalid response format: missing data');
      }

      // Normalizar resposta do Spring Data Pageable para nosso formato
      const responseData = response.data as any;
      
      // Se a resposta tem o formato Spring Data (content, pageable, totalPages)
      if (responseData.content && responseData.pageable) {
        // Normalizar dados dos grupos (converter status para lowercase)
        const normalizedGroups = responseData.content.map((group: any) => ({
          ...group,
          id: group.id?.toString() || group.id, // Garantir que ID é string
          status: statusFromAPI(group.status || 'INACTIVE'), // Converter status para lowercase
        }));

        return {
          data: normalizedGroups,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Se a resposta não tiver a estrutura de paginação, criar uma
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        return {
          data: items,
          pagination: {
            page: params.page,
            size: params.size,
            total: items.length,
            totalPages: Math.ceil(items.length / params.size) || 1,
            hasNext: params.page < Math.ceil(items.length / params.size) - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Group by ID
   * Fetches detailed information about a specific group
   * 
   * @param id - Group ID
   * @returns Promise with group details
   */
  getGroupById: async (id: string): Promise<GroupListItem> => {
    try {
      const response = await api.get<GroupListItem>(`/groups/${id}`);
      
      // Normalize status from API response
      const group = response.data;
      return {
        ...group,
        status: statusFromAPI(group.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Group by Reference (UUID)
   * Fetches detailed information about a specific group using reference
   * More secure than using ID as it doesn't expose sequential IDs
   * 
   * @param reference - Group reference (UUID)
   * @returns Promise with group details
   */
  getGroupByReference: async (reference: string): Promise<GroupListItem> => {
    try {
      const response = await api.get<GroupListItem>(`/groups/reference/${reference}`);
      
      // Normalize status from API response
      const group = response.data;
      return {
        ...group,
        status: statusFromAPI(group.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Create Group
   * Creates a new group in the system
   * 
   * @param groupData - Group data for creation
   * @returns Promise with created group
   */
  createGroup: async (groupData: CreateGroupPayload): Promise<GroupListItem> => {
    try {
      // Convert status to uppercase for API
      const apiPayload = {
        ...groupData,
        status: groupData.status ? statusToAPI(groupData.status) : 'ACTIVE',
      };
      
      const response = await api.post<GroupListItem>('/groups', apiPayload);
      
      // Normalize status from API response
      const group = response.data;
      return {
        ...group,
        status: statusFromAPI(group.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Update Group
   * Updates an existing group's information
   * 
   * @param id - Group ID
   * @param groupData - Partial group data to update
   * @returns Promise with updated group
   */
  updateGroup: async (
    id: string,
    groupData: UpdateGroupPayload
  ): Promise<GroupListItem> => {
    try {
      // Convert status to uppercase for API if provided
      const apiPayload = {
        ...groupData,
        ...(groupData.status && { status: statusToAPI(groupData.status) }),
      };
      
      const response = await api.put<GroupListItem>(
        `/groups/${id}`,
        apiPayload
      );
      
      // Normalize status from API response
      const group = response.data;
      return {
        ...group,
        status: statusFromAPI(group.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Delete Group
   * Deletes a group from the system
   * 
   * @param id - Group ID
   * @returns Promise that resolves when group is deleted
   */
  deleteGroup: async (id: string): Promise<void> => {
    try {
      await api.delete(`/groups/${id}`);
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Update Group Status
   * Changes group status (active, inactive)
   * 
   * @param id - Group ID
   * @param status - New status
   * @returns Promise with updated group
   */
  updateGroupStatus: async (
    id: string,
    status: string
  ): Promise<GroupListItem> => {
    try {
      // Convert status to uppercase for API
      const response = await api.patch<GroupListItem>(
        `/groups/${id}/status`,
        { status: statusToAPI(status) }
      );
      
      // Normalize status from API response
      const group = response.data;
      return {
        ...group,
        status: statusFromAPI(group.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Bulk Delete Groups
   * Deletes multiple groups at once
   * 
   * @param ids - Array of group IDs to delete
   * @returns Promise that resolves when groups are deleted
   */
  bulkDeleteGroups: async (ids: string[]): Promise<void> => {
    try {
      await api.post('/groups/bulk-delete', { ids });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Export Groups
   * Exports groups list to CSV/Excel
   * 
   * @param filters - Optional filters for export
   * @returns Promise with file blob
   */
  exportGroups: async (filters?: GroupFilters): Promise<Blob> => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters?.status && { status: filters.status }),
        ...(filters?.organizationId && { 
          organizationId: filters.organizationId.toString() 
        }),
        ...(filters?.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters?.dateTo && { dateTo: filters.dateTo }),
      });

      const response = await api.get(
        `/groups/export?${queryParams.toString()}`,
        {
          responseType: 'blob',
        }
      );

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Link Agent to Group
   * Associates an agent with a group
   * 
   * @param groupId - Group ID
   * @param agentId - Agent ID to link
   * @returns Promise that resolves when agent is linked
   */
  linkAgent: async (groupId: string, agentId: string): Promise<void> => {
    try {
      await api.post(`/groups/${groupId}/agents`, { agentId });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Unlink Agent from Group
   * Removes an agent from a group
   * 
   * @param groupId - Group ID
   * @param agentId - Agent ID to unlink
   * @returns Promise that resolves when agent is unlinked
   */
  unlinkAgent: async (groupId: string, agentId: string): Promise<void> => {
    try {
      await api.delete(`/groups/${groupId}/agents/${agentId}`);
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Link Certificate to Group
   * Associates a certificate with a group
   * 
   * @param groupId - Group ID
   * @param certificateId - Certificate ID to link
   * @returns Promise that resolves when certificate is linked
   */
  linkCertificate: async (groupId: string, certificateId: string): Promise<void> => {
    try {
      await api.post(`/groups/${groupId}/certificates`, { certificateId });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Unlink Certificate from Group
   * Removes a certificate from a group
   * 
   * @param groupId - Group ID
   * @param certificateId - Certificate ID to unlink
   * @returns Promise that resolves when certificate is unlinked
   */
  unlinkCertificate: async (groupId: string, certificateId: string): Promise<void> => {
    try {
      await api.delete(`/groups/${groupId}/certificates/${certificateId}`);
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Group Agents (Paginated)
   * Fetches paginated list of agents linked to a specific group
   * 
   * @param groupId - Group ID
   * @param params - Pagination parameters
   * @returns Promise with paginated agent list
   */
  getGroupAgents: async (
    groupId: string,
    params: PaginationParams
  ): Promise<PaginatedResponse<AgentListItem>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
      });

      const response = await api.get<PaginatedResponse<AgentListItem>>(
        `/groups/${groupId}/agents?${queryParams.toString()}`
      );

      // Normalizar resposta do Spring Data Pageable
      const responseData = response.data as any;
      
      if (responseData.content && responseData.pageable) {
        const normalizedAgents = responseData.content.map((agent: any) => ({
          ...agent,
          id: agent.id?.toString() || agent.id,
          status: statusFromAPI(agent.status || 'OFFLINE'),
        }));

        return {
          data: normalizedAgents,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Fallback se não tiver paginação
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        return {
          data: items,
          pagination: {
            page: params.page,
            size: params.size,
            total: items.length,
            totalPages: Math.ceil(items.length / params.size) || 1,
            hasNext: params.page < Math.ceil(items.length / params.size) - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Group Certificates (Paginated)
   * Fetches paginated list of certificates linked to a specific group
   * 
   * @param groupId - Group ID
   * @param params - Pagination parameters
   * @returns Promise with paginated certificate list
   */
  getGroupCertificates: async (
    groupId: string,
    params: PaginationParams
  ): Promise<PaginatedResponse<CertificateListItem>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
      });

      const response = await api.get<PaginatedResponse<CertificateListItem>>(
        `/groups/${groupId}/certificates?${queryParams.toString()}`
      );

      // Normalizar resposta do Spring Data Pageable
      const responseData = response.data as any;
      
      if (responseData.content && responseData.pageable) {
        return {
          data: responseData.content,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Fallback se não tiver paginação
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        return {
          data: items,
          pagination: {
            page: params.page,
            size: params.size,
            total: items.length,
            totalPages: Math.ceil(items.length / params.size) || 1,
            hasNext: params.page < Math.ceil(items.length / params.size) - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Group Stats
   * Fetches statistics about a group (counts of agents and certificates)
   * 
   * @param groupId - Group ID
   * @returns Promise with group statistics
   */
  getGroupStats: async (groupId: string): Promise<GroupStats> => {
    try {
      const response = await api.get<GroupStats>(`/groups/${groupId}/stats`);
      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Available Agents (Not Linked to Group)
   * Fetches paginated list of agents that are NOT linked to a specific group
   * Used for adding agents to the group
   * 
   * @param groupId - Group ID
   * @param params - Pagination parameters
   * @returns Promise with paginated agent list
   */
  getAvailableAgents: async (
    groupId: string,
    params: PaginationParams
  ): Promise<PaginatedResponse<AgentListItem>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
        ...(params.search && { search: params.search }),
        ...((params as any).hostname && { hostname: (params as any).hostname }),
      });

      const finalUrl = `/groups/${groupId}/available-agents?${queryParams.toString()}`;
      
      const response = await api.get<PaginatedResponse<AgentListItem>>(finalUrl);

      // Normalizar resposta do Spring Data Pageable
      const responseData = response.data as any;
      
      if (responseData.content && responseData.pageable) {
        const normalizedAgents = responseData.content.map((agent: any) => ({
          ...agent,
          id: agent.id?.toString() || agent.id,
          status: statusFromAPI(agent.status || 'OFFLINE'),
        }));

        return {
          data: normalizedAgents,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Fallback se não tiver paginação
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        return {
          data: items,
          pagination: {
            page: params.page,
            size: params.size,
            total: items.length,
            totalPages: Math.ceil(items.length / params.size) || 1,
            hasNext: params.page < Math.ceil(items.length / params.size) - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Available Certificates (Not Linked to Group)
   * Fetches paginated list of certificates that are NOT linked to a specific group
   * Used for adding certificates to the group
   * 
   * @param groupId - Group ID
   * @param params - Pagination parameters
   * @returns Promise with paginated certificate list
   */
  getAvailableCertificates: async (
    groupId: string,
    params: PaginationParams
  ): Promise<PaginatedResponse<CertificateListItem>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
        ...(params.search && { search: params.search }),
      });

      const response = await api.get<PaginatedResponse<CertificateListItem>>(
        `/groups/${groupId}/available-certificates?${queryParams.toString()}`
      );

      // Normalizar resposta do Spring Data Pageable
      const responseData = response.data as any;
      
      if (responseData.content && responseData.pageable) {
        return {
          data: responseData.content,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Fallback se não tiver paginação
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        return {
          data: items,
          pagination: {
            page: params.page,
            size: params.size,
            total: items.length,
            totalPages: Math.ceil(items.length / params.size) || 1,
            hasNext: params.page < Math.ceil(items.length / params.size) - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },
};

export default groupService;
